import { useCallback, useEffect, useState } from "react";

import { Spinner } from "@vega-core/components/Loaders/LoadingSpinner/Spinner";

export function useInfiniteScroll(
  fetchNextPage: () => void,
  hasNextPage: boolean,
  isFetchingNextPage: boolean,
) {
  const [sentinelRef, setSentinelRef] = useState<HTMLDivElement | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection);

    if (sentinelRef) {
      observer.observe(sentinelRef);
    }

    return () => {
      if (sentinelRef) {
        observer.unobserve(sentinelRef);
      }
    };
  }, [sentinelRef, handleIntersection]);

  // Create and return a sentinel element (empty div)
  const sentinelElement = (
    <div
      className="my-4"
      data-testid="infinite-scroll-sentinel"
      ref={setSentinelRef}
    >
      {isFetchingNextPage ? (
        <Spinner asIcon data-testid="infinite-scroll-sentinel-spinner" />
      ) : null}
    </div>
  );

  return sentinelElement;
}
